import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, firstValueFrom, Observable, Subscription } from 'rxjs';
import { businessMessage } from '@callcenter-app/components/order-live/order-http-error';
import { ShellCall } from './call-state.machine';
import { CALL_STATE_CLOCK, CallStateService } from './call-state.service';
import { CALL_LINK_ACTIONS, OrderActed, OrderActedHook, OrderActedKind } from './order-acted.token';
import { WORKPLACE_READY } from './workplace.config';
import { WorkplaceCallLink, WorkplaceService } from './workplace.service';

/**
 * The link of a call to an order WRITES (W3), so it goes live with the rest of the stage and not before: the one switch of the stage (`WORKPLACE_READY`). Off — no action links anything, and the form of a new order does not know
 * the held call (S1 is idle): the pages are as they were. A spec gives its own value and the line of the stand never changes what the specs prove.
 */
export const CALL_LINK_ENABLED = new InjectionToken<boolean>('cc.callLink.enabled', { providedIn: 'root', factory: () => WORKPLACE_READY });

/** What the operator did (the words of the window say it): an action of the token, or the making of an order owed to a call. */
export type LinkDone = OrderActedKind | 'created';

/** The window «Привязать текущий звонок к заказу №…?» — the call is from a number that is not the order's. */
export interface LinkDialogRequest {
  callId: string;
  orderId: number;
  orderNumber: string;
  /** The number of the call, digits. */
  phone: string;
  done: LinkDone;
}

/** The first seconds of a conversation: the exchange pushes the event before it writes the call to the database, so W3 may not see the call yet (S5). */
export const LINK_RETRY_WINDOW_MS = 10_000;
/** The one retry on `workplace.callNotCurrent` inside that window. */
export const LINK_RETRY_DELAY_MS = 2000;
/** The one retry on `workplace.busy` (the row of the call was locked for 2 s). */
export const LINK_BUSY_RETRY_MS = 1500;

type Decision = 'linked' | 'declined';

/** What came of a question: the operator's word, or `stale` — the call it was about is no more the held, talking one (nothing is decided then, and nothing is remembered). */
type Answered = 'accepted' | 'declined' | 'stale';

type Sent =
  | { readonly kind: 'answer'; readonly link: WorkplaceCallLink }
  | { readonly kind: 'failed'; readonly message: string; readonly max?: number };

/**
 * The link of the call the operator is talking on to the orders he works with (stage 4, design §3.5). The owner's rule: the order made within the call is linked at once (the editor does it, E11); ANY OTHER action with an order during
 * the call — the change, the transfer, the cancel, the invoice, the comment — links it too: the same number (the client's or the recipient's) — at once, with a toast; another number — a window asks, once per order and call.
 *
 * `acted()` is the token {@link ORDER_ACTED}: the places of the actions call it after the action SUCCEEDED, the rest is here.
 *  - S5: only while the call the workplace holds is talking (on the operator's number — the shell listens to that number only); W3 says `workplace.callNotCurrent` otherwise — silently skipped, with ONE retry inside the first 10 s of the conversation;
 *  - S8: one record per (call, order) — a request in flight is shared, so a second invoice (or an invoice after the change) neither asks again nor opens a second window; what is decided (linked / declined) is remembered
 *    for the call and forgotten with it;
 *  - W3 does not take a reservation the call does not hold (`ordersArchive.orderNotFound`) — silently skipped;
 *  - B2′ (the owner): `created` and `released` are the operator closing an order by himself — after the link is dealt with, the shell is told (`CallStateService.orderClosed`), and a call that waits for the order of the held one opens;
 *  - a question lives as long as its call is the held one and talks: when the held call changes or ends, the questions about it are taken away (answered `stale`, not recorded as «Не привязывать»), the window shows the next one
 *    that is still valid or closes; and a confirmation is sent only if its call is still the held, talking one at that very moment.
 *
 * An action does not wait for any of this and is never undone by it: a failed link is a word to the operator, not an error of the action.
 */
@Injectable({ providedIn: 'root' })
export class CallLinkService implements OrderActedHook, OnDestroy {
  private readonly dialogSubject = new BehaviorSubject<LinkDialogRequest | undefined>(undefined);
  /** The window that is open: the first of the queue. */
  readonly dialog$: Observable<LinkDialogRequest | undefined> = this.dialogSubject.asObservable();

  private readonly decided = new Map<string, Decision>();
  private readonly inFlight = new Map<string, Promise<void>>();
  /** The orders made WITHOUT the call that was held (S1): order id → the call they are owed to. */
  private readonly owed = new Map<number, string>();
  private asked: Array<{ request: LinkDialogRequest; answer: (answered: Answered) => void }> = [];
  private epoch = 0;
  private subs = new Subscription();

  constructor(
    private workplace: WorkplaceService,
    private callState: CallStateService,
    private toastr: ToastrService,
    private tr: TranslocoService,
    @Inject(CALL_STATE_CLOCK) private clock: () => number,
    @Inject(CALL_LINK_ENABLED) private enabled: boolean,
  ) {
    if (enabled) {
      this.subs.add(this.callState.state$.subscribe(state => this.dropStale(state.held)));
    }
  }

  /** Resolves when everything the event asked for is over (the window answered included) — the places of the actions do not wait for it. */
  acted(event: OrderActed): Promise<void> {
    if (!this.enabled) {
      return Promise.resolve();
    }
    if (event.kind === 'created' || event.kind === 'cancel') {
      // the count of the client's orders («15 заказов» on the card of the call and the band) moved: it is read again, and the panel of the workplace reads the same (F8)
      this.callState.refreshOrdersTotal(undefined, true);
    }
    // what the event asks of the link goes first (it reads the call that is held NOW)…
    const done = this.handle(event);
    if (event.kind === 'created' || event.kind === 'released') {
      // …and then the order is closed by the operator himself: a call that waits for it opens (B2′), the one that was held is not the workplace's any more
      this.callState.orderClosed(event.orderId);
    }
    return done;
  }

  private handle(event: OrderActed): Promise<void> {
    switch (event.kind) {
      case 'bookedApart':
        this.owed.set(event.orderId, event.callId);
        return Promise.resolve();
      case 'created': {
        const callId = this.owed.get(event.orderId);
        this.owed.delete(event.orderId);
        const held = this.callState.snapshot.held;
        return callId !== undefined && held?.callId === callId && held.phase === 'talking' ? this.link(held, event.orderId, 'created', event.uniqueNumber) : Promise.resolve();
      }
      case 'released':
        return Promise.resolve();
      default: {
        const held = this.callState.snapshot.held;
        return CALL_LINK_ACTIONS.includes(event.kind) && held?.phase === 'talking' ? this.link(held, event.orderId, event.kind, event.uniqueNumber) : Promise.resolve();
      }
    }
  }

  heldCallId(): string | undefined {
    return this.enabled ? this.callState.snapshot.held?.callId : undefined;
  }

  /** The window was answered: «Привязать» (`true`) or «Не привязывать». */
  answer(accept: boolean): void {
    const first = this.asked.shift();
    this.dialogSubject.next(this.asked[0]?.request);
    first?.answer(accept ? 'accepted' : 'declined');
  }

  /** Sign-out: nothing of the session stays. */
  purge(): void {
    this.epoch++;
    const asked = this.asked;
    this.asked = [];
    this.dialogSubject.next(undefined);
    asked.forEach(item => item.answer('stale'));
    this.decided.clear();
    this.inFlight.clear();
    this.owed.clear();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ---------- one record per (call, order) ----------

  private link(call: ShellCall, orderId: number, done: LinkDone, uniqueNumber?: string): Promise<void> {
    this.forgetOtherCalls(call.callId);
    const key = this.keyOf(call.callId, orderId);
    if (this.decided.has(key)) {
      return Promise.resolve();
    }
    const running = this.inFlight.get(key);
    if (running) {
      return running;
    }
    const run = this.run(call, orderId, done, uniqueNumber).finally(() => this.inFlight.delete(key));
    this.inFlight.set(key, run);
    return run;
  }

  private async run(call: ShellCall, orderId: number, done: LinkDone, uniqueNumber?: string): Promise<void> {
    const epoch = this.epoch;
    const key = this.keyOf(call.callId, orderId);
    const first = await this.send(call, orderId, false);
    if (epoch !== this.epoch) {
      return;
    }
    if (first.kind === 'failed') {
      this.report(first, orderId, uniqueNumber, false);
      return;
    }
    const number = first.link.orderNumber || uniqueNumber || String(orderId);
    if (first.link.linked) {
      this.linked(key, first.link, number);
      return;
    }
    if (first.link.samePhone) {
      return;
    }
    // the call may have ended or been replaced while the first request was on its way: nobody is asked about a call that is gone
    if (!this.isCurrent(call.callId)) {
      return;
    }
    // the number of the call is not the order's: the operator decides, and the decision stays for this call
    const answered = await this.ask({ callId: call.callId, orderId, orderNumber: number, phone: call.phone, done });
    if (epoch !== this.epoch || answered === 'stale') {
      return;
    }
    if (answered === 'declined') {
      this.decided.set(key, 'declined');
      return;
    }
    // the confirmation (`confirmDifferentPhone`) is sent only for the call that is still the held, talking one at this very moment; otherwise it is dropped in silence
    if (!this.isCurrent(call.callId)) {
      return;
    }
    const second = await this.send(call, orderId, true);
    if (epoch !== this.epoch) {
      return;
    }
    if (second.kind === 'failed') {
      this.report(second, orderId, number, true);
      return;
    }
    this.linked(key, second.link, second.link.orderNumber || number);
  }

  private linked(key: string, link: WorkplaceCallLink, number: string): void {
    this.decided.set(key, 'linked');
    if (!link.already) {
      this.toastr.success(this.tr.translate('callLink.linked', { num: number }));
    }
  }

  // ---------- W3 ----------

  /** W3, with the retries that belong to it: the first seconds of a conversation (the call is not in the database yet), a row that was locked. A retry is made only if the call is still the held one and still talks. */
  private async send(call: ShellCall, orderId: number, confirm: boolean): Promise<Sent> {
    const first = await this.post(call.callId, orderId, confirm);
    if (first.kind !== 'failed') {
      return first;
    }
    const wait = this.retryAfter(first.message, call);
    if (wait === undefined) {
      return first;
    }
    await new Promise<void>(resolve => setTimeout(resolve, wait));
    const held = this.callState.snapshot.held;
    return held?.callId === call.callId && held.phase === 'talking' ? this.post(call.callId, orderId, confirm) : first;
  }

  private retryAfter(message: string, call: ShellCall): number | undefined {
    if (message === 'workplace.busy') {
      return LINK_BUSY_RETRY_MS;
    }
    if (message === 'workplace.callNotCurrent' && call.talkAt !== undefined && this.clock() - call.talkAt <= LINK_RETRY_WINDOW_MS) {
      return LINK_RETRY_DELAY_MS;
    }
    return undefined;
  }

  private async post(callId: string, orderId: number, confirm: boolean): Promise<Sent> {
    try {
      return { kind: 'answer', link: await firstValueFrom(this.workplace.linkCall(callId, orderId, confirm)) };
    } catch (error) {
      const err = error instanceof HttpErrorResponse ? error : undefined;
      const data: unknown = (err?.error as { data?: unknown } | null | undefined)?.data;
      const max = data !== null && typeof data === 'object' ? (data as { max?: unknown }).max : undefined;
      return { kind: 'failed', message: businessMessage(err) ?? '', ...(typeof max === 'number' ? { max } : {}) };
    }
  }

  /**
   * What the operator is told when a link was not made. The skipped ones are quiet when nobody asked for the link — the call has ended or is not on this number, the order is a reservation the call does not hold; when the operator
   * pressed «Привязать» they are said. Nothing is remembered: the next action with the order asks again.
   */
  private report(failure: Extract<Sent, { kind: 'failed' }>, orderId: number, uniqueNumber: string | undefined, asked: boolean): void {
    const num = uniqueNumber || String(orderId);
    switch (failure.message) {
      case 'workplace.callNotCurrent':
        if (asked) {
          this.toastr.warning(this.tr.translate('workplace.callNotCurrent'));
        }
        return;
      case 'ordersArchive.orderNotFound':
        if (asked) {
          this.toastr.warning(this.tr.translate('callLink.failed', { num }));
        }
        return;
      case 'workplace.busy':
        this.toastr.warning(this.tr.translate('workplace.busy'));
        return;
      case 'workplace.linkLimit':
        this.toastr.warning(this.tr.translate('workplace.linkLimit', { max: failure.max ?? 50 }));
        return;
      default:
        this.toastr.warning(this.tr.translate('callLink.failed', { num }));
    }
  }

  // ---------- the window ----------

  private ask(request: LinkDialogRequest): Promise<Answered> {
    return new Promise<Answered>(resolve => {
      this.asked.push({ request, answer: resolve });
      if (this.asked.length === 1) {
        this.dialogSubject.next(request);
      }
    });
  }

  /** The held call changed or left the conversation: the questions about any other call are taken away — answered `stale`, so «Не привязывать» is not recorded — and the window shows the next one that is valid or closes. */
  private dropStale(held: ShellCall | undefined): void {
    if (this.asked.length === 0) {
      return;
    }
    const stale = this.asked.filter(item => !this.isCurrentOf(held, item.request.callId));
    if (stale.length === 0) {
      return;
    }
    const shown = this.asked[0];
    this.asked = this.asked.filter(item => !stale.includes(item));
    if (this.asked[0] !== shown) {
      this.dialogSubject.next(this.asked[0]?.request);
    }
    stale.forEach(item => item.answer('stale'));
  }

  /** The call is the held one and talks, now. */
  private isCurrent(callId: string): boolean {
    return this.isCurrentOf(this.callState.snapshot.held, callId);
  }

  private isCurrentOf(held: ShellCall | undefined, callId: string): boolean {
    return held?.callId === callId && held.phase === 'talking';
  }

  // ---------- the memory ----------

  private keyOf(callId: string, orderId: number): string {
    return `${callId}|${orderId}`;
  }

  /** What was decided for a call goes with the call: a new held call starts with a clean memory. */
  private forgetOtherCalls(callId: string): void {
    const prefix = `${callId}|`;
    [...this.decided.keys()].filter(key => !key.startsWith(prefix)).forEach(key => this.decided.delete(key));
  }
}
